/** Curate facet bar (facets 1c-2, spec §5). Presentational: AngleManager owns the flows. */
import type { ReactNode } from "react";
import { AlertTriangle, ChevronDown, History } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRIDGES } from "@/lib/angles";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import type { FacetDetail, FacetSummary } from "@/types/concept";

interface Props {
  facets: FacetSummary[];
  retired: FacetDetail[];
  /** angleCounts(): facet id -> members, plus "bridges". */
  counts: Record<string, number>;
  seedCounts: Record<string, number>;
  selected: string | undefined;
  /** A facet op is in flight (or the Add-angle sheet is open): the angle
   *  menus are inert. Tabs stay live — filtering doesn't change angles. */
  disabled: boolean;
  /** Why `disabled` (defaults to BUSY_REASON). */
  disabledReason?: string;
  /** Angle pass retries exhausted: each angle tab carries a warning. The
   *  status itself, with Retry, lives in the operation bar (opbar 1a). */
  failed?: boolean;
  onSelect: (angle: string | undefined) => void;
  onEdit: (facet: FacetSummary) => void;
  onRetire: (facet: FacetSummary) => void;
  onRestore: (facet: FacetDetail) => void;
  onMove: (facetId: string, delta: -1 | 1) => void;
  /** Re-seed one angle (AddAngleSheet `seeds` mode). */
  onFindSeeds?: (facet: FacetSummary) => void;
  /** Trawl from this angle's seeds only. */
  onTrawlAngle?: (facet: FacetSummary) => void;
  /** A trawl is running: "Trawl this angle" is disabled (one trawl at a time). */
  trawling?: boolean;
  /** 1c-3 slot ("+ Add an angle"); when given, the bar renders at 1 angle too. */
  trailing?: ReactNode;
}

function Tab({ active, onClick, label, children }: { active: boolean; onClick: () => void; label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}

/** Why angle controls are dim while another angle change saves (useFacetBusy). */
export const BUSY_REASON = "Wait for the angle change to finish.";

export function FacetBar({
  facets, retired, counts, seedCounts, selected, disabled, disabledReason = BUSY_REASON, failed,
  onSelect, onEdit, onRetire, onRestore, onMove, onFindSeeds, onTrawlAngle, trawling = false, trailing,
}: Props) {
  const multi = facets.length >= 2;
  if (!multi && retired.length === 0 && !trailing) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-1 rounded-md border bg-muted/30 px-2 py-1.5" role="toolbar" aria-label="Angles">
      {multi && (
        <>
          <Tab active={!selected} onClick={() => onSelect(undefined)} label="All angles">All angles</Tab>
          {facets.map((f, i) => (
            <span key={f.id} className="inline-flex items-center">
              <Tab active={selected === f.id} onClick={() => onSelect(f.id)} label={`${f.label} ${counts[f.id] ?? 0}`}>
                {f.label}
                <span className="tabular-nums opacity-70">{counts[f.id] ?? 0}</span>
                {failed && (
                  <span
                    role="img"
                    aria-label={`${f.label} scores are out of date`}
                    title={`${f.label} scores couldn't refresh — Retry in the bar below`}
                  >
                    <AlertTriangle className="size-3 text-amber-400" />
                  </span>
                )}
                {(seedCounts[f.id] ?? 0) === 0 && (
                  <span title={`No seeds for ${f.label} yet`} className="size-1.5 rounded-full bg-amber-500" />
                )}
              </Tab>
              <WhyDisabled reason={disabled ? disabledReason : null}>
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label={`${f.label} options`}
                  disabled={disabled}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ChevronDown className="size-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onSelect={() => onEdit(f)}>Edit angle…</DropdownMenuItem>
                  {onFindSeeds && <DropdownMenuItem onSelect={() => onFindSeeds(f)}>Find seeds…</DropdownMenuItem>}
                  {onTrawlAngle && (
                    <DropdownMenuItem
                      disabled={trawling} onSelect={() => onTrawlAngle(f)}
                      aria-describedby={trawling ? `trawl-why-${f.id}` : undefined}
                      className="flex-col items-start gap-0 data-[disabled]:opacity-100"
                    >
                      <span className={trawling ? "opacity-50" : undefined}>Trawl this angle</span>
                      {/* Hidden from the item's name, read as its description. */}
                      {trawling && (
                        <span id={`trawl-why-${f.id}`} aria-hidden className="text-[11px] text-muted-foreground">
                          A trawl is already running.
                        </span>
                      )}
                    </DropdownMenuItem>
                  )}
                  {i > 0 && <DropdownMenuItem onSelect={() => onMove(f.id, -1)}>Move left</DropdownMenuItem>}
                  {i < facets.length - 1 && <DropdownMenuItem onSelect={() => onMove(f.id, 1)}>Move right</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onRetire(f)}>Retire…</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </WhyDisabled>
            </span>
          ))}
          <Tab active={selected === BRIDGES} onClick={() => onSelect(BRIDGES)} label={`Bridges ${counts[BRIDGES] ?? 0}`}>
            Bridges <span className="tabular-nums opacity-70">{counts[BRIDGES] ?? 0}</span>
          </Tab>
        </>
      )}
      {retired.length > 0 && (
        <WhyDisabled reason={disabled ? disabledReason : null} className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            <History className="size-3" /> Retired angles ({retired.length})
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {retired.map((f) => (
              <DropdownMenuItem key={f.id} onSelect={() => onRestore(f)}>Restore {f.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        </WhyDisabled>
      )}
      {trailing}
    </div>
  );
}
